<template>
    <b-button
        v-if="relations.length === 1"
        size="sm"
        :disabled="!hasSelection"
        @click="$_openRelation(relations[0])"
    >
        <img
            src="~@/assets/imported-icons/ic_fluent_link_square_20_regular.svg"
            :alt="relationsTitle"
            :title="buttonTitle(!hasSelection, relationsTitle)"
        />
    </b-button>
    <b-dropdown
        v-else
        size="sm"
        toggle-class="custom-button"
        :disabled="!hasSelection"
    >
        <template #button-content>
            <img
                src="~@/assets/imported-icons/ic_fluent_link_square_20_regular.svg"
                :alt="relationsTitle"
                :title="buttonTitle(!hasSelection, relationsTitle)"
            />
        </template>
        <b-dropdown-item
            v-for="relation in relations"
            :key="relation.relationId"
            @click="$_openRelation(relation)"
            :title="
                buttonTitle(
                    !hasSelection,
                    `${$t('relations.showRelated')} ${relation.name}`
                )
            "
            >{{ relation.name }}</b-dropdown-item
        >
    </b-dropdown>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex';
    import { RELATION_TYPES, RELATIONSHIP_TYPES } from '../../../../constants';

    export default {
        props: {
            exportData: { type: Array },
            featureData: { type: Array },
            relations: { type: Array, required: true },
            layer: { type: Object, required: false, default: null },
            isTableResultTab: { type: Boolean, default: false }
        },
        computed: {
            ...mapGetters(['getLayerByOriginalId', 'getTableSearchResultById']),
            hasSelection: function () {
                return this.exportData?.length > 0 || false;
            },
            buttonTitle() {
                return (isDisabled, title) =>
                    isDisabled ? `${title}\n(${this.$t('selectRows')})` : title;
            },
            relationsTitle() {
                return this.relations.length === 1
                    ? `${this.$t('relations.showRelated')} ${
                          this.relations[0].name
                      }`
                    : this.$t('relations.showRelations');
            }
        },
        methods: {
            ...mapActions([
                'findReversedTableRelation',
                'findDataRelation',
                'findSpatialRelation',
                'findThemeLayer2TableRelation',
                'findTable2TableRelation',
                'clearTabsRight',
                'setTabToFocus'
            ]),
            $_openRelation(relation) {
                // if started from tableresult tab, then open the reversed relation
                // We delete all data to the right of this tab.
                // We give tabToFocus a value so tabs.vue knows to set focus to the last tab.

                // To determine the current tab name.
                // If tableresult then get the name from the tableSearchResult
                // else get the layer name
                let currentTabName = '';
                if (this.isTableResultTab) {
                    const tableSearchresult = this.getTableSearchResultById(
                        relation.toTableSearchResultId
                    );
                    currentTabName = tableSearchresult?.title;
                } else {
                    currentTabName = this.layer?.data?.title;
                }

                this.clearTabsRight({ title: currentTabName });
                const relationTabName = this.$_getRelationTabName(relation);
                // Delete existing tabs with the same name
                this.$store.commit('CLEAR_SELECTION_DATA', {
                    title: relationTabName
                });
                this.setTabToFocus({ title: relationTabName });
                if (this.isTableResultTab) {
                    if (relation.type === RELATIONSHIP_TYPES.ThemeLayer2Table) {
                        this.findReversedTableRelation({
                            data: this.exportData,
                            relation: { ...relation }
                        });
                    }
                    if (relation.type === RELATIONSHIP_TYPES.Table2Table) {
                        this.findTable2TableRelation({
                            data: this.exportData,
                            relation: { ...relation }
                        });
                    }
                    return;
                }
                if (relation.type === RELATIONSHIP_TYPES.ThemeLayer2Table) {
                    this.findThemeLayer2TableRelation({
                        relation,
                        objectData: this.$_getObjectData(relation),
                        features: this.featureData,
                        computedStyles: this.layer.extended.computedStyles
                    });
                } else if (relation.relationType === RELATION_TYPES.Normal) {
                    const multipleFilters = this.exportData.length > 1;
                    this.findDataRelation({
                        relation,
                        filters: multipleFilters
                            ? this.$_getFilterData(relation)
                            : this.$_getFilterData(relation)[0],
                        multipleFilters,
                        tabName: relationTabName
                    });
                } else {
                    this.findSpatialRelation({
                        relation,
                        features: this.featureData,
                        tabName: relationTabName
                    });
                }
            },
            $_getObjectData(relation) {
                const objectData = this.exportData.map((object) => {
                    return relation.settings.items.map((relationItem) => ({
                        caseSensitive: relationItem.caseSensitive,
                        sourceFieldName: relationItem.sourceFieldName,
                        sourceFieldValue:
                            object[relationItem.sourceFieldName]?.value?.toString()
                    }));
                });
                // get unique values from array of arrays
                const set = new Set(objectData.map(JSON.stringify));
                return Array.from(set).map(JSON.parse);
            },

            $_getRelationTabName(relation) {
                const layer = this.getLayerByOriginalId(
                    relation.targetThemeLayerId
                );
                if (layer) {
                    return layer.data.title;
                } else {
                    return relation.name;
                }
            },
            $_getFilterData(relation) {
                const objectData = this.exportData.map((object) => {
                    return relation.settings.items
                        .filter(
                            (relationItem) =>
                                object[relationItem.sourceFieldName]?.value
                        )
                        .map((relationItem) => ({
                            caseSensitive: relationItem.caseSensitive,
                            filterAttribute: relationItem.destinationFieldName,
                            operator: '=',
                            value: object[relationItem.sourceFieldName]?.value,
                            visible: true,
                            dataType: `xsd:${relationItem.dataType}`
                        }));
                });
                // get unique values from array of arrays
                const set = new Set(objectData.map(JSON.stringify));
                return Array.from(set).map(JSON.parse);
            }
        }
    };
</script>

<style lang="scss" scoped>
.btn {
    border: 1px solid transparent;

    &:disabled {
        color: $gray-04;
        background-color: transparent;
        border: 1px solid transparent;
    }
}
</style>
